import React, { useState, useEffect } from 'react';
import { X, Heart, MessageCircle, Share2, Bookmark, Eye, Trash2, ShoppingCart, Flag, AlertTriangle, Check, Clock, Edit } from 'lucide-react';
// Previously unused imports removed: Download, ChevronLeft, ChevronRight
import { Post, Comment } from '../types';
import { postsService } from '../services/postsService';
import { useAuth } from '../context/AuthContext';
import MediaViewer from './MediaViewer';
import ShareModal from './ShareModal';
import EditPostModal from './EditPostModal';
import PurchaseModal from './PurchaseModal';
import CommentSection from './CommentSection';
import ReportDialog from './ReportDialog';
import { reportsService } from '../services/reportsService';
import { moderationService } from '../services/moderationService';
import { useLanguage } from '../context/LanguageContext';

interface PostDetailModalProps {
  post: Post;
  onClose: () => void;
  onLike: (postId: string) => void;
  onSave: (postId: string) => void;
  onDelete?: (postId: string) => void;
}

const PostDetailModal: React.FC<PostDetailModalProps> = ({ post, onClose, onLike, onSave, onDelete }) => {
  const { user } = useAuth();
  const { select, locale } = useLanguage();
  const [currentPost, setCurrentPost] = useState<Post>(post);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes || post.likes_count || 0);
  const [comments, setComments] = useState<Comment[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [reportFeedback, setReportFeedback] = useState('');
  const [moderating, setModerating] = useState(false);
  const [moderationError, setModerationError] = useState('');



  const isModerator = user?.user_type === 'moderator' || user?.user_type === 'admin';
  const isAuthor = user?.id === currentPost?.author?.id;
  const moderationStatus = currentPost?.moderation_status || 'approved';
  const isPending = moderationStatus === 'pending';
  const isRejected = moderationStatus === 'rejected';
  const showModerationBanner = (isPending || isRejected) && (isAuthor || isModerator);

  useEffect(() => {
    setCurrentPost(post);
    setLikesCount(post.likes || post.likes_count || 0);
    setComments(post.comments || []);
    setReportFeedback('');
    setModerationError('');
    void loadPostDetails();
    void checkIfLiked();
    void checkIfSaved();
    void incrementViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  const loadPostDetails = async () => {
    try {
      const bypass = (user?.id === post.author?.id) || (user?.user_type === 'moderator' || user?.user_type === 'admin');
      const detailedPost = await postsService.getPostById(post.id, { bypassModeration: bypass });
      setCurrentPost(prev => {
        const previous = prev || post;
        const detailed = detailedPost as Record<string, unknown>;
        const previousAny = previous as Record<string, unknown>;
        const detailedMedia = Array.isArray(detailed.media) ? detailed.media : Array.isArray(detailed.media_files) ? detailed.media_files : null;
        const previousMedia = Array.isArray(previousAny.media) ? previousAny.media : Array.isArray(previousAny.media_files) ? previousAny.media_files : null;
        const normalizedMedia = (detailedMedia ?? previousMedia ?? post.media ?? []) as unknown[];
        const normalizedAuthor = detailed.author ?? detailed.profiles ?? previousAny.author ?? post.author;

        return {
          ...previous,
          ...(detailedPost as Partial<Post>),
          author: normalizedAuthor,
          media: normalizedMedia,
        } as Post;
      });
      const detailedComments = (detailedPost as Record<string, unknown>).comments;
      setComments(Array.isArray(detailedComments) ? (detailedComments as Comment[]) : []);
      setModerationError('');
    } catch (err: unknown) {
      console.error('Error loading post details:', err);
      const message =
        err instanceof Error
          ? err.message
          : select({
              es: 'No es posible mostrar esta publicación en este momento.',
              en: 'We cannot display this post right now.',
            });
      setModerationError(message);
    } finally {
      setLoading(false);
    }
  };

  const checkIfLiked = async () => {
    try {
      const liked = await postsService.checkIfLiked(post.id);
      setIsLiked(liked);
    } catch (err) {
      console.error('Error checking like status:', err);
    }
  };

  const checkIfSaved = async () => {
    try {
      const saved = await postsService.checkIfSaved(post.id);
      setIsSaved(saved);
    } catch (err) {
      console.error('Error checking save status:', err);
    }
  };

  const incrementViews = async () => {
    // Only increment views if this is the first time opening detail modal
    // PostCard already increments views, so we don't double count
    return;
  };

  const handleLike = () => {
    if (isLiked) {
      // Unlike
      setIsLiked(false);
      setLikesCount(prev => Math.max(0, prev - 1));
    } else {
      // Like
      setIsLiked(true);
      setLikesCount(prev => prev + 1);
    }

    // Call parent handler
    onLike(currentPost.id);
  };

  const handleSave = () => {
    const newSavedState = !isSaved;

    // Update local state immediately for better UX
    setIsSaved(newSavedState);

    if (newSavedState) {
      // Add to wishlist
      postsService.addToWishlist(currentPost.id).catch(err => {
        console.error('Error adding to wishlist:', err);
        // Revert state if error
        setIsSaved(false);
      });
    } else {
      // Remove from wishlist
      postsService.removeFromWishlist(currentPost.id).catch(err => {
        console.error('Error removing from wishlist:', err);
        // Revert state if error
        setIsSaved(true);
      });
    }

    onSave(currentPost.id);
  };

  const handleCommentsUpdate = (updatedComments: Comment[]) => {
    setComments(updatedComments);
  };

  const handleDelete = async () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await postsService.deletePost(currentPost.id);
      onClose();
      if (onDelete) {
        onDelete(currentPost.id);
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      alert(
        select({ es: 'Error al eliminar la publicación: ', en: 'Failed to delete the post: ' }) +
        (err instanceof Error ? err.message : select({ es: 'Error desconocido', en: 'Unknown error' }))
      );
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const handleReportSubmit = async ({ reason, description }: { reason: string; description?: string }) => {
    await reportsService.createReport({
      itemType: 'post',
      itemId: currentPost.id,
      reason,
      description,
    });
    setReportFeedback(select({
      es: 'Reporte enviado correctamente. Gracias por ayudarnos a mantener la comunidad segura.',
      en: 'Report sent successfully. Thanks for helping us keep the community safe.',
    }));
    setTimeout(() => setReportFeedback(''), 6000);
  };

  const handleModerationAction = async (action: 'approve' | 'reject' | 'pending') => {
    try {
      let reason: string | undefined;

      if (action === 'reject') {
        reason = window.prompt('Motivo del rechazo', currentPost.moderation_reason || '') || undefined;
        if (!reason) {
          return;
        }
      }

      setModerating(true);
      setModerationError('');

      const updatedPost = await moderationService.moderatePost({
        postId: currentPost.id,
        action,
        reason,
      });

      if (updatedPost) {
        setCurrentPost(updatedPost as Post);
        const maybeComments = (updatedPost as Record<string, unknown>).comments;
        setComments(Array.isArray(maybeComments) ? (maybeComments as Comment[]) : comments);
      }

      const message = action === 'approve'
        ? select({ es: 'Publicación aprobada correctamente.', en: 'Post approved successfully.' })
        : action === 'reject'
          ? select({ es: 'Publicación rechazada y oculta del feed.', en: 'Post rejected and hidden from the feed.' })
          : select({ es: 'Publicación marcada nuevamente como pendiente.', en: 'Post marked as pending again.' });
      setReportFeedback(message);
      setTimeout(() => setReportFeedback(''), 4000);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : select({
              es: 'No fue posible actualizar el estado de la publicación.',
              en: 'Unable to update the post status.',
            });
      setModerationError(message);
    } finally {
      setModerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md transition-opacity duration-300" onClick={onClose} />
      <div className="relative w-full h-full md:h-[95vh] md:max-h-[900px] md:max-w-7xl md:mx-6 bg-white md:rounded-[2rem] shadow-2xl overflow-hidden flex flex-col md:flex-row animate-in fade-in zoom-in-95 duration-200 ring-1 ring-white/10">
        {/* Media Viewer - Left Side */}
        <div className="flex-1 bg-black relative order-1 md:order-1 h-[50vh] md:h-auto">
          {currentPost ? (
            <MediaViewer post={currentPost} />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-black text-white">
              {select({ es: 'Cargando publicación...', en: 'Loading post...' })}
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 left-3 p-2 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-all z-20 md:top-4 md:left-4"
          >
            <X size={20} className="md:w-6 md:h-6" />
          </button>

          {/* Delete Button (only for own posts) */}
          {user && currentPost.author && (user.id === currentPost.author.id || user.username === currentPost.author.username) && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="absolute top-3 right-3 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-all z-20 disabled:opacity-50 shadow-lg"
              title={select({ es: 'Eliminar publicación', en: 'Delete post' })}
            >
              {deleting ? '...' : <Trash2 size={18} className="md:w-5 md:h-5" />}
            </button>
          )}
        </div>

        {/* Details Panel - Right Side */}
        <div className="w-full md:w-[420px] lg:w-[480px] flex flex-col relative order-2 md:order-2 h-[50vh] md:h-full bg-white transition-all duration-300" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="p-5 md:p-6 border-b border-gray-100 flex-shrink-0 backdrop-blur-sm bg-white/80 sticky top-0 z-10">
            <div
              className="flex items-center gap-4 cursor-pointer group/user"
              onClick={() => {
                window.location.href = `/profile/${currentPost.author?.username ?? post.author?.username ?? ''}`;
              }}
            >
              <div className="relative">
                <div className="absolute -inset-0.5 bg-gradient-to-tr from-purple-500 to-pink-500 rounded-full opacity-0 group-hover/user:opacity-100 transition-opacity duration-300 blur-[2px]"></div>
                <img
                  src={currentPost.author?.avatar_url || post.author?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
                  alt={currentPost.author?.full_name || post.author?.full_name || 'User'}
                  className="relative w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-white shadow-sm"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 leading-tight group-hover/user:text-purple-600 transition-colors truncate">
                  {currentPost.author?.full_name || post.author?.full_name || select({ es: 'Usuario', en: 'User' })}
                </h3>
                <p className="text-xs text-gray-500 font-medium truncate">
                  @{currentPost.author?.username || post.author?.username || 'user'}
                </p>
              </div>
              {currentPost.for_sale && (
                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold text-gray-900 mb-1">{formatPrice(currentPost.price!)}</div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPurchaseModal(true);
                    }}
                    className="px-4 py-1.5 bg-gray-900 text-white text-xs font-bold rounded-full hover:bg-gray-800 transition-all hover:scale-105 shadow-md flex items-center gap-1.5"
                  >
                    <ShoppingCart size={12} />
                    {select({ es: 'Comprar', en: 'Buy' })}
                  </button>
                </div>
              )}
            </div>
          </div>

          {moderationError && (
            <div className="mx-3 md:mx-4 mt-3 px-4 py-3 bg-red-50 border border-red-200 text-sm text-red-800 rounded-lg">
              {moderationError}
            </div>
          )}

          {showModerationBanner && (
            <div
              className={`mx-3 md:mx-4 mt-3 px-4 py-3 rounded-xl border text-sm ${isPending
                ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                : 'bg-red-50 border-red-200 text-red-800'
                }`}
            >
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className={isPending ? 'text-yellow-500 mt-0.5' : 'text-red-500 mt-0.5'} />
                <div>
                  <p className="font-medium">
                    {isPending
                      ? select({
                        es: 'Esta publicación está en revisión por el equipo de moderación.',
                        en: 'This post is under review by the moderation team.',
                      })
                      : select({
                        es: 'Esta publicación fue rechazada y no es visible públicamente.',
                        en: 'This post was rejected and is not publicly visible.',
                      })}
                  </p>
                  {currentPost.moderation_reason && (
                    <p className="mt-1 text-sm opacity-80">
                      {select({ es: 'Motivo:', en: 'Reason:' })} {currentPost.moderation_reason}
                    </p>
                  )}
                  {currentPost.moderated_at && (
                    <p className="mt-1 text-xs opacity-60">
                      {select({ es: 'Última revisión:', en: 'Last review:' })} {new Date(currentPost.moderated_at).toLocaleString(locale)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isModerator && (
            <div className="mx-3 md:mx-4 mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => handleModerationAction('approve')}
                disabled={moderating || moderationStatus === 'approved'}
                className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-xs md:text-sm rounded-lg hover:bg-green-700 disabled:opacity-60"
              >
                <Check size={16} />
                {select({ es: 'Aprobar', en: 'Approve' })}
              </button>
              <button
                onClick={() => handleModerationAction('reject')}
                disabled={moderating}
                className="inline-flex items-center gap-2 px-3 py-2 bg-red-600 text-white text-xs md:text-sm rounded-lg hover:bg-red-700 disabled:opacity-60"
              >
                <X size={16} />
                {select({ es: 'Rechazar', en: 'Reject' })}
              </button>
              <button
                onClick={() => handleModerationAction('pending')}
                disabled={moderating || moderationStatus === 'pending'}
                className="inline-flex items-center gap-2 px-3 py-2 bg-amber-500 text-white text-xs md:text-sm rounded-lg hover:bg-amber-600 disabled:opacity-60"
              >
                <Clock size={16} />
                {select({ es: 'Marcar pendiente', en: 'Mark pending' })}
              </button>
            </div>
          )}

          {reportFeedback && (
            <div className="mx-3 md:mx-4 mt-3 px-4 py-2 bg-green-50 border border-green-200 text-sm text-green-700 rounded-lg">
              {reportFeedback}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0 pb-safe">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight">{currentPost.title}</h2>
            <p className="text-base text-gray-600 mb-6 leading-relaxed font-light whitespace-pre-line">{currentPost.description}</p>

            {/* Tags */}
            {currentPost.tags && currentPost.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {currentPost.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-50 border border-gray-200 text-gray-600 text-xs font-medium rounded-full hover:border-purple-300 hover:text-purple-600 transition-colors cursor-default"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between py-2 md:py-3 border-t border-gray-200 mb-3 md:mb-4">
              <div className="flex items-center gap-3 md:gap-4">
                {isAuthor && (
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors font-medium"
                    title={select({ es: 'Editar publicación', en: 'Edit post' })}
                  >
                    <Edit size={18} className="md:w-5 md:h-5" />
                    <span className="hidden sm:inline text-xs md:text-sm">{select({ es: 'Editar', en: 'Edit' })}</span>
                  </button>
                )}
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 transition-colors ${isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                    }`}
                >
                  <Heart size={18} className="md:w-5 md:h-5" fill={isLiked ? 'currentColor' : 'none'} />
                  <span className="text-xs md:text-sm font-medium">{likesCount}</span>
                </button>

                <button className="flex items-center gap-2 text-gray-500 hover:text-blue-500 transition-colors">
                  <MessageCircle size={18} className="md:w-5 md:h-5" />
                  <span className="text-xs md:text-sm font-medium">{comments.length}</span>
                </button>

                <button
                  onClick={() => setShowShareModal(true)}
                  className="flex items-center gap-2 text-gray-500 hover:text-green-500 transition-colors"
                >
                  <Share2 size={18} className="md:w-5 md:h-5" />
                </button>

                {!isAuthor && (
                  <button
                    onClick={() => setShowReportModal(true)}
                    className="flex items-center gap-2 text-gray-500 hover:text-red-500 transition-colors"
                    title={select({ es: 'Reportar publicación', en: 'Report post' })}
                  >
                    <Flag size={18} className="md:w-5 md:h-5" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2 md:gap-3">
                <div className="flex items-center gap-1 text-gray-500">
                  <Eye size={14} className="md:w-4 md:h-4" />
                  <span className="text-xs md:text-sm">{currentPost.views || currentPost.views_count}</span>
                </div>

                <button
                  onClick={handleSave}
                  className={`transition-colors ${isSaved ? 'text-purple-500' : 'text-gray-500 hover:text-purple-500'
                    }`}
                >
                  <Bookmark size={18} className="md:w-5 md:h-5" fill={isSaved ? 'currentColor' : 'none'} />
                </button>
              </div>
            </div>

            {/* Comments */}
            <div className="pb-4">
              <CommentSection
                postId={currentPost.id}
                comments={comments}
                onCommentsUpdate={handleCommentsUpdate}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10001]">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="text-red-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {select({ es: '¿Eliminar publicación?', en: 'Delete post?' })}
                </h3>
                <p className="text-gray-600">
                  {select({
                    es: 'Esta acción no se puede deshacer. Se eliminará permanentemente tu obra y todos los comentarios.',
                    en: 'This action cannot be undone. Your artwork and all comments will be permanently deleted.',
                  })}
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="text-red-600 mt-0.5">⚠️</div>
                  <div>
                    <h4 className="font-medium text-red-900 mb-1">
                      {select({ es: 'Se eliminará permanentemente:', en: 'The following will be removed permanently:' })}
                    </h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>
                        • {select({ es: 'La publicación', en: 'The post' })} "{currentPost.title}"
                      </li>
                      <li>
                        • {select({ es: 'Todos los comentarios', en: 'All comments' })} ({comments.length})
                      </li>
                      <li>
                        • {select({ es: 'Los likes recibidos', en: 'Received likes' })} ({currentPost.likes || currentPost.likes_count})
                      </li>
                      <li>• {select({ es: 'Los guardados', en: 'Saved entries' })}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 font-medium"
                >
                  {select({ es: 'Cancelar', en: 'Cancel' })}
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {select({ es: 'Eliminando...', en: 'Deleting...' })}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      {select({ es: 'Eliminar definitivamente', en: 'Delete permanently' })}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {/* Share Modal with higher z-index */}
      {showShareModal && typeof document !== 'undefined' && (
        <div className="fixed inset-0 z-[10000]">
          <ShareModal
            post={currentPost}
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
          />
        </div>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <PurchaseModal
          post={currentPost}
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          onSuccess={() => {
            console.log('Purchase completed successfully!');
            setShowPurchaseModal(false);
            // Optionally close the detail modal after successful purchase
            // onClose();
          }}
        />
      )}

      <ReportDialog
        isOpen={showReportModal}
        title={select({ es: 'Reportar publicación', en: 'Report post' })}
        itemDescription={currentPost.title}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleReportSubmit}
      />

      {/* Edit Post Modal */}
      {showEditModal && (
        <EditPostModal
          isOpen={showEditModal}
          post={currentPost}
          onClose={() => setShowEditModal(false)}
          onSaved={(updated) => {
            setCurrentPost(updated as Post);
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
};

export default PostDetailModal;
